import { useMemo } from 'react';
import { useHistory } from 'react-router';
import { getNumberWithCommas } from 'utils';
import { CampaignStatus, URLPaths } from 'models/enums';
import useAudienceCount, { UseAudienceCountWithChannelResponse } from 'hooks/queries/useAudienceCount';
import useCampaigns from 'hooks/queries/useCampaigns';
import useSegment from 'hooks/queries/useSegment';
import { Campaign } from 'domains/campaigns/types';
import DetailItem from 'domains/core/components/DetailItem';
import AutocompleteWithClickAway from 'domains/core/components/AutocompleteWithClickAway';
import useSegmentCohort from 'hooks/queries/useSegmentCohort';
import AbTestingStatusDetails from './CustomCampaignDetailsPage/AbTestingStatusDetails/AbTestingStatusDetails';
import { Box } from '@mui/material';
import CampaignStatusText from '../CampaignsTable/CampaignStatusText';

type Props = {
    campaign: Campaign;
    contentPreviewNodeId?: string | number;
    handlePreviewClick?: (contentNodeId: number) => void;
    onChange?: () => void;
};

const CurationListAndFirstAudience = ({ campaign, onChange, contentPreviewNodeId, handlePreviewClick }: Props) => {
    const { cohortId } = campaign.audiences?.[0] ?? {};
    const { segmentId } = campaign.sends?.[0] ?? {};

    const { data: segment, isSuccess: isSegmentSuccess } = useSegment(segmentId, {
        enabled: Boolean(segmentId),
    });
    const { data: cohort, isSuccess: isCohortSuccess } = useSegmentCohort(cohortId, {
        enabled: Boolean(cohortId),
    });

    const { data: segmentCount, isSuccess: isSegmentCountSuccess } = useAudienceCount(segmentId, campaign.channel, {
        enabled: Boolean(segmentId),
    });

    const history = useHistory();
    const { data: campaigns } = useCampaigns();

    const campaignOptions = useMemo(() => campaigns?.map((campaign) => ({ id: campaign.id, name: campaign.name })), [
        campaigns,
    ]);

    const countData = segmentCount as UseAudienceCountWithChannelResponse;

    const audienceDetailName = isSegmentSuccess ? segment.name : isCohortSuccess ? cohort.name : '';
    const segmentDetailCount = isSegmentCountSuccess ? getNumberWithCommas(Number(countData?.count)) : '';

    const audienceDescription = `${audienceDetailName} ${segmentDetailCount && `(${segmentDetailCount})`}`;

    return (
        <>
            <AutocompleteWithClickAway
                header="Curation"
                description={campaign.name}
                options={campaignOptions}
                option={campaign}
                optionLabelKey="name"
                optionValueKey="id"
                onChange={(option) => {
                    history.push(`${URLPaths.CURATIONS}/${option.value}/summary`);
                    onChange && onChange();
                }}
            />
            <AbTestingStatusDetails
                campaign={campaign}
                contentPreviewNodeId={contentPreviewNodeId}
                handlePreviewClick={handlePreviewClick}
            />

            {!campaign.abTestConfig && (
                <Box sx={{ pt: 1 }}>
                    <CampaignStatusText status={campaign.status as CampaignStatus} />
                </Box>
            )}
            <DetailItem header="Audience" description={audienceDescription} />
        </>
    );
};

export default CurationListAndFirstAudience;
